import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Router, NavigationEnd } from '@angular/router';
import { WhatsAppTriggerService } from './whatsapp-trigger.service';

export type TriggerPriority = 'normal' | 'high' | 'highest';
export type TriggerRouting = 'bot-first' | 'kushi-direct' | 'interakt-proactive';

export interface TriggerDef {
  id: string;
  name: string;
  page: string;
  priority: TriggerPriority;
  routing: TriggerRouting;
  condition: string;        // Why fired
  autoMessage: string;      // Pre-filled WhatsApp text
  quickReplies: string[];
  trainerNote?: string;
}

// export const TRIGGERS: Record<string, TriggerDef> = {
//   T1: {
//     id: 'T1',
//     name: 'Wall Size Confusion',
//     page: 'PDP',
//     priority: 'normal',
//     routing: 'bot-first',
//     condition: 'Customer pauses 20+ seconds on the calculator without submitting, or clears and re-enters dimensions.',
//     autoMessage: 'Hi!  I need help calculating my wall size for this wallpaper. Can you guide me through the dimensions (feet vs inches)?',
//     quickReplies: ['Feet+Inches', 'Inches only', 'Talk to Kushi']
//   },
//   T2: {
//     id: 'T2',
//     name: 'Surface Confusion',
//     page: 'PDP',
//     priority: 'normal',
//     routing: 'bot-first',
//     condition: 'Customer switches surface 2+ times within 2 minutes — confused about surface options.',
//     autoMessage: 'Hi!  I\'m confused between the surface options (Glitter / Premium Canvas / Non-Woven). Can you help me pick the right one?',
//     quickReplies: ['Compare all', 'Premium Canvas', 'Talk to Kushi']
//   },
//   T3: {
//     id: 'T3',
//     name: 'Mockup Request',
//     page: 'PDP',
//     priority: 'high',
//     routing: 'kushi-direct',
//     condition: 'Customer cycles through gallery images 3+ times or zooms into a room mockup — wants to visualize on their wall.',
//     autoMessage: 'Hi!  I\'d love to see this wallpaper on my actual wall before buying. Can I share my room photo for a custom mockup?',
//     quickReplies: ['Send room photo', 'See more designs', 'Pricing']
//   },
//   T4: {
//     id: 'T4',
//     name: 'Add to Cart Hesitation',
//     page: 'PDP',
//     priority: 'normal',
//     routing: 'bot-first',
//     condition: 'Desktop: hovered Add to Cart 10+ seconds without clicking. Mobile: tapped → scrolled away → returned within 60s.',
//     autoMessage: 'Hi!  I\'m considering this wallpaper but have some questions before adding to cart — about EMI, delivery time, or installation. Can you help?',
//     quickReplies: ['EMI options', 'Delivery time', 'Installation help', 'Talk to Kushi']
//   },
//   T5: {
//     id: 'T5',
//     name: 'High Value Customer',
//     page: 'PDP',
//     priority: 'highest',
//     routing: 'kushi-direct',
//     condition: '200+ sq.ft entered or 3+ walls added — this is a ₹50K+ project (VIP).',
//     autoMessage: 'Hi !  I\'m planning a large wallpaper project (200+ sq.ft / multiple walls). Can we discuss volume pricing, premium options, and installation? Need senior expert help.',
//     quickReplies: ['Schedule call today', 'Tomorrow morning', 'Send pricing first']
//   },
//   T6: {
//     id: 'T6',
//     name: 'Installation Not Serviceable',
//     page: 'PDP',
//     priority: 'high',
//     routing: 'kushi-direct',
//     condition: 'Pincode check returned "installation not available" — needs alternate solution.',
//     autoMessage: 'Hi !  My pincode shows installation isn\'t available. Can you suggest alternates — DIY kit, partner installer, or Self-Adhesive surface?',
//     quickReplies: ['DIY Kit', 'Partner installer', 'Self-Adhesive']
//   },
//   T7: {
//     id: 'T7',
//     name: 'Browsing But Not Engaging',
//     page: 'Listing',
//     priority: 'normal',
//     routing: 'bot-first',
//     condition: '90+ seconds on listing page with zero product clicks — overwhelmed by choices.',
//     autoMessage: 'Hi!  So many wallpaper options — can you help me narrow down by Style (Modern/Floral/Abstract), Room (Living/Bedroom), or Budget?',
//     quickReplies: ['By Style', 'By Room', 'By Budget', 'Talk to Kushi']
//   },
//   T8: {
//     id: 'T8',
//     name: 'Return Visitor With PDP History',
//     page: 'Homepage',
//     priority: 'high',
//     routing: 'interakt-proactive',
//     condition: 'Returning visitor previously viewed a product but did not add to cart — now 30+ seconds on homepage.',
//     autoMessage: 'Hi!  I\'m back — I was looking at a wallpaper earlier. Can you show me what\'s available with current offers?',
//     quickReplies: ['Continue previous', 'Show new arrivals', 'Pricing']
//   }
// };

export const TRIGGERS: Record<string, TriggerDef> = {
  T1: {
    id: 'T1',
    name: 'Wall Size Confusion',
    page: 'PDP',
    priority: 'normal',
    routing: 'bot-first',
    condition: 'Customer pauses 20+ seconds on the calculator without submitting, or clears and re-enters dimensions.',
    autoMessage: 'What are your wall dimensions — height and width, in feet or inches.',
    quickReplies: ['Feet+Inches', 'Inches only', 'Talk to Kushi']
  },
  T2: {
    id: 'T2',
    name: 'Surface Confusion',
    page: 'PDP',
    priority: 'normal',
    routing: 'bot-first',
    condition: 'Customer switches surface 2+ times within 2 minutes — confused about surface options.',
    autoMessage: 'The surface choice depends on your wall type and the finish you want. Tell us about your wall.',
    quickReplies: ['Compare all', 'Premium Canvas', 'Talk to Kushi']
  },
  T3: {
    id: 'T3',
    name: 'Mockup Request',
    page: 'PDP',
    priority: 'high',
    routing: 'kushi-direct',
    condition: 'Customer cycles through gallery images 3+ times or zooms into a room mockup — wants to visualize on their wall.',
    autoMessage: 'Share a photo of the space and the wall dimensions — we will show you, how it looks exactly.',
    quickReplies: ['Send room photo', 'See more designs', 'Pricing']
  },
  T4: {
    id: 'T4',
    name: 'Add to Cart Hesitation',
    page: 'PDP',
    priority: 'normal',
    routing: 'bot-first',
    condition: 'Desktop: hovered Add to Cart 10+ seconds without clicking. Mobile: tapped → scrolled away → returned within 60s.',
    autoMessage: 'Anything about the material, installation, or delivery that would be useful to know? We are here.',
    quickReplies: ['EMI options', 'Delivery time', 'Installation help', 'Talk to Kushi']
  },
  T5: {
    id: 'T5',
    name: 'Looking for Wallpaper for Multiple Walls or Large Spaces?',
    page: 'PDP',
    priority: 'highest',
    routing: 'kushi-direct',
    condition: '200+ sq.ft entered or 3+ walls added — this is a ₹50K+ project (VIP).',
    autoMessage: 'The right recommendation needs a proper look at the space. Share your wall details and we will take it from there.',
    quickReplies: ['Schedule call today', 'Tomorrow morning', 'Send pricing first']
  },
  T6: {
    id: 'T6',
    name: 'Installation Not Serviceable',
    page: 'PDP',
    priority: 'high',
    routing: 'kushi-direct',
    condition: 'Pincode check returned "installation not available" — needs alternate solution.',
    autoMessage: "Installation isn't available at your pincode yet. Let’s find the best solution for your wall.",
    quickReplies: ['DIY Kit', 'Partner installer', 'Self-Adhesive']
  },
  T7: {
    id: 'T7',
    name: 'Browsing But Not Engaging',
    page: 'Listing',
    priority: 'normal',
    routing: 'bot-first',
    condition: '90+ seconds on listing page with zero product clicks — overwhelmed by choices.',
    autoMessage: 'Your wall comes first. Tell us the size and mood, and we’ll curate the options.',
    quickReplies: ['By Style', 'By Room', 'By Budget', 'Talk to Kushi']
  },
  T8: {
    id: 'T8',
    name: 'Return Visitor With PDP History',
    page: 'Homepage',
    priority: 'high',
    routing: 'interakt-proactive',
    condition: 'Returning visitor previously viewed a product but did not add to cart — now 30+ seconds on homepage.',
    autoMessage: 'Good to see you again, We’re here to help you find the right art.',
    quickReplies: ['Continue previous', 'Show new arrivals', 'Pricing']
  }
};

@Injectable({ providedIn: 'root' })
export class TriggerService {
  private currentSubject = new BehaviorSubject<TriggerDef | null>(null);
  current$ = this.currentSubject.asObservable();

  private cooldown: Record<string, number> = {};
  private readonly COOLDOWN_MS = 30000;
  /** Visible nudge auto-hides after this many ms */
  private readonly NUDGE_VISIBLE_MS = 120000;
  private nudgeHideTimer: any = null;

  // Detection state
  private t1Timer: any = null;
  private t2Switches: number[] = [];
  private t3ThumbClicks = 0;
  private t3LastReset = 0;
  private t4HoverTimer: any = null;
  private t5Walls = 0;
  private t7Timer: any = null;
  private t8Timer: any = null;

  constructor(private whatsapp: WhatsAppTriggerService, private zone: NgZone, private router: Router) {
    this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe((e: any) => {
      const url: string = e.urlAfterRedirects || e.url || '';
      // Always dismiss on cart/checkout
      if (url.startsWith('/cart') || url.startsWith('/checkout')) {
        this.dismiss();
        return;
      }
      // Dismiss PDP-scoped triggers when leaving PDP pages so the footer
      // WhatsApp button doesn't pre-fill a PDP message on listing/home etc.
      const curr = this.currentSubject.value;
      if (curr && curr.page === 'PDP') {
        const onPdp = url.startsWith('/wallpaper/') || url.startsWith('/pdp/');
        if (!onPdp) this.dismiss();
      }
    });
  }

  /**
   * Fire a trigger — emits to subscribers (footer pulse, etc.) and stores the
   * WhatsApp pre-fill. Visible nudge auto-hides after NUDGE_VISIBLE_MS.
   * The WhatsApp pre-fill keeps its own freshness window inside WhatsAppTriggerService.
   */
  fire(id: string, force = false) {
    const trig = TRIGGERS[id];
    if (!trig) return;
    const now = Date.now();
    if (!force && this.cooldown[id] && now - this.cooldown[id] < this.COOLDOWN_MS) return;
    this.cooldown[id] = now;
    this.currentSubject.next(trig);
    this.whatsapp.setActiveTrigger(trig);

    // Auto-hide the visible nudge after 10s (clear any prior timer first)
    if (this.nudgeHideTimer) clearTimeout(this.nudgeHideTimer);
    this.nudgeHideTimer = setTimeout(() => {
      this.zone.run(() => {
        this.currentSubject.next(null);
        this.nudgeHideTimer = null;
      });
    }, this.NUDGE_VISIBLE_MS);
  }

  dismiss() {
    if (this.nudgeHideTimer) { clearTimeout(this.nudgeHideTimer); this.nudgeHideTimer = null; }
    this.currentSubject.next(null);
    this.whatsapp.clearActiveTrigger();
  }

  /** Open WhatsApp directly for the active trigger */
  accept() {
    const trig = this.currentSubject.value;
    if (!trig) return;
    this.whatsapp.openWhatsApp(trig);
    this.dismiss();
  }

  // ============ PDP DETECTORS (T1-T6) ============

  /**
   * T1: Wall Size Confusion
   * Fires 20s after the customer **interacts** with width/height inputs but
   * doesn't successfully fill both. Does NOT fire if customer never engaged
   * with the size calculator (avoids false-positive on browse-only visitors).
   */
  private t1GetStatus: (() => { widthFilled: boolean; heightFilled: boolean }) | null = null;

  /** Call once on PDP enter — registers status getter, no timer yet */
  pdpStartSizeWatch(getStatus: () => { widthFilled: boolean; heightFilled: boolean }) {
    this.pdpStopSizeWatch();
    this.t1GetStatus = getStatus;
    // Reset wall counter so it doesn't accumulate across PDP navigations
    this.t5Walls = 0;
  }

  /**
   * Call when user interacts with width/height input (focus/click/type).
   * Starts the 20s confusion timer; re-arms on every interaction so the
   * "clear + re-enter dimensions" scenario also gets the full 20s window.
   */
  pdpSizeInteracted() {
    if (!this.t1GetStatus) return;
    if (this.t1Timer) clearTimeout(this.t1Timer);
    this.t1Timer = setTimeout(() => {
      const s = this.t1GetStatus?.();
      if (s && !(s.widthFilled && s.heightFilled)) this.fire('T1');
    }, 20000);
  }

  pdpStopSizeWatch() {
    if (this.t1Timer) { clearTimeout(this.t1Timer); this.t1Timer = null; }
    this.t1GetStatus = null;
  }

  /** T2: 2+ surface switches in 2 min (per spec) */
  pdpRecordSurfaceSwitch() {
    const now = Date.now();
    this.t2Switches.push(now);
    this.t2Switches = this.t2Switches.filter(t => now - t < 120000);
    if (this.t2Switches.length >= 2) {
      this.fire('T2');
      this.t2Switches = [];
    }
  }

  /** T3: 3+ thumbnail cycles within 2 min */
  pdpRecordThumbClick() {
    const now = Date.now();
    if (now - this.t3LastReset > 120000) this.t3ThumbClicks = 0;
    this.t3LastReset = now;
    this.t3ThumbClicks++;
    if (this.t3ThumbClicks >= 3) {
      this.fire('T3');
      this.t3ThumbClicks = 0;
    }
  }

  /** T4: hover 10s+ on Add to Cart (desktop) */
  pdpAddCartHoverStart() {
    this.pdpAddCartHoverEnd();
    this.t4HoverTimer = setTimeout(() => this.fire('T4'), 10000);
  }
  pdpAddCartHoverEnd() {
    if (this.t4HoverTimer) { clearTimeout(this.t4HoverTimer); this.t4HoverTimer = null; }
  }

  /** T5: high-value (200+ sqft or 3+ walls) */
  pdpCheckHighValue(opts: { sqft?: number; wallCount?: number }) {
    if ((opts.sqft && opts.sqft >= 200) || (opts.wallCount && opts.wallCount >= 3)) {
      this.fire('T5');
    }
  }
  pdpAddWall() {
    this.t5Walls++;
    if (this.t5Walls >= 3) {
      this.fire('T5');
      this.t5Walls = 0;
    }
  }

  /** T6: pincode unserviceable */
  pdpPincodeNotServiceable() {
    this.fire('T6', true);
  }

  // ============ LISTING T7 ============
  listingStartWatch() {
    this.listingStopWatch();
    this.t7Timer = setTimeout(() => this.fire('T7'), 90000);
  }
  listingStopWatch() {
    if (this.t7Timer) { clearTimeout(this.t7Timer); this.t7Timer = null; }
  }
  listingProductClicked() { this.listingStopWatch(); }

  // ============ HOMEPAGE T8 ============
  homepageStartWatch() {
    let pdpVisited = false;
    let cartHasItem = false;
    try {
      pdpVisited = sessionStorage.getItem('mahattaPdpVisited') === '1';
      cartHasItem = sessionStorage.getItem('mahattaCartHadItem') === '1';
    } catch {}
    if (!pdpVisited || cartHasItem) return;
    this.homepageStopWatch();
    this.t8Timer = setTimeout(() => this.fire('T8'), 30000);
  }
  homepageStopWatch() {
    if (this.t8Timer) { clearTimeout(this.t8Timer); this.t8Timer = null; }
  }
  markPdpVisited() {
    try { sessionStorage.setItem('mahattaPdpVisited', '1'); } catch {}
  }
  markCartAdded() {
    try { sessionStorage.setItem('mahattaCartHadItem', '1'); } catch {}
  }
}
